import * as React from "react";
import { toast } from "react-toastify";
import { CellContext } from "@tanstack/react-table";

import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import { ITableInstance } from "src/library/components/Tables/Table";
import DropInfoSidebar from "src/core/drops/components/DropInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { useDrop } from "src/core/drops/hooks";
import { PageProps } from "src/pages/Router/routes";
import {
  IUser,
  useUserApi
} from "src/core/users/api/users.api";
import ActionButton, { IAction } from "src/library/components/Button/ActionButton";
import { Modal } from "src/library/components/Modal";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { LikeToDo } from "src/library/components/LikeToDo";
import { Button } from "src/library/components/Button";
import { getUserTableColumns } from "src/core/shared/components/tables/usersTableColumns";
import { Table } from "src/core/dashboard/components/widgets";

interface UrlParams {
  dropId: string;
  customerId: string;
}

const defaultPagination: PaginatedParams<IUser> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "firstName", "ASC" ] ]
};

const DropUsersPage: React.FunctionComponent<PageProps<unknown, UrlParams>> = props => {
  const {
    history, terminology, match
  } = props;

  const TableRef = React.useRef<ITableInstance<IUser>>(null);
  const dropId = match ? parseInt(match.params.dropId, 0) : 0;
  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const [ { isLoading: isLoadingDrop, drop } ] = useDrop(dropId);
  const [ usersResponse, getUsers ] = useUserApi("LIST_BRANCH_USERS");
  const [ suspendUserResponse, suspendUser ] = useUserApi("UPDATE_USER");
  const [ resendInviteResponse, resendInvite ] = useUserApi("RESEND_INVITE");
  const [ deleteUserResponse, deleteUser ] = useUserApi("DELETE_USER");
  const [ deleteUserModalData, setDeleteUserModalData ] = React.useState<{userId: number} | null>(null);
  const checkPermission = useCheckUserPermission();

  const onFetchData = React.useCallback((params: {filters: FilterParams<IUser>, pagination: PaginatedParams<IUser>}) => {
    getUsers({
      pagination: params.pagination,
      filters: {
        ...params.filters,
        defaultCustomerId: { $not: null }
      },
      dropId
    });
  }, [ dropId, getUsers ]);

  // on suspend update
  React.useEffect(() => {
    if (suspendUserResponse.data) {
      toast.success(`The ${terminology.user.singular.text} has been successfully updated.`);

      if (TableRef.current) {
        TableRef.current.reloadTable(true);
      }
    }

    if (suspendUserResponse.error) {
      toast.error(`Could not update ${terminology.user.singular.text} - ${suspendUserResponse.error.message}`);
    }
  }, [ suspendUserResponse, terminology.user.singular.text ]);

  // on resend invite
  React.useEffect(() => {
    if (resendInviteResponse.data) {
      toast.success("The invite has been sent");
    }

    if (resendInviteResponse.error) {
      toast.error(`Invite not sent ${terminology.user.singular.text} - ${resendInviteResponse.error.message}`);
    }
  }, [ resendInviteResponse, terminology.user.singular.text ]);

  // On user deletion
  React.useEffect(() => {
    if (deleteUserResponse.data) {
      toast.success(`The ${terminology.user.singular.text} has been successfully deleted.`);

      if (TableRef.current) {
        TableRef.current.reloadTable(true);
      }
    }

    if (deleteUserResponse.error) {
      toast.error(`Could not delete ${terminology.user.singular.text} - ${deleteUserResponse.error.message}`);
    }
  }, [ deleteUserResponse, terminology.user.singular.text ]);

  const createNewUser = React.useCallback(() => {
    const link = (
      <Button
        onClick={() => {
          history.push(`/admin/users/customers/${customerId}/create`, { dropId });
        }}
      >
        {`Create new ${terminology.user.singular.text}`}
      </Button>
    );

    const buttons = [ link ] as JSX.Element[];

    return buttons;
  }, [
    customerId,
    dropId,
    history,
    terminology.user.singular.text
  ]);

  const userTableActionsButton = React.useCallback((info: CellContext<IUser, unknown>) => {
    const items: IAction[] = [
      {
        title: "Edit",
        disabled: !checkPermission("amend-user"),
        action: () => {
          history.push(`/admin/customers/users/${info.row.original.id}/details`);
        }
      },
      {
        title: `${info.row.original.status === "active" ? "Suspend" : "Activate"}`,
        disabled: !checkPermission("amend-user"),
        action: () => {
          suspendUser({
            id: info.row.original.id,
            status: info.row.original.status === "active" ? "suspended" : "active"
          });
        }
      },
      {
        title: "Delete",
        disabled: !checkPermission("delete-user"),
        action: () => {
          setDeleteUserModalData({ userId: info.row.original.id });
        }
      }
    ];

    if (info.row.original.lastLoggedIn === null) {
      items.push({
        disabled: !checkPermission("create-user"),
        title: "Resend invite",
        action: () => resendInvite({ id: info.row.original.id })
      });
    }

    return (
      <ActionButton
        title="Actions"
        items={items}
      />
    );
  }, [
    checkPermission,
    history,
    resendInvite,
    suspendUser
  ]);

  const columns = React.useMemo(() => {
    return getUserTableColumns({
      terminology,
      actionButton: userTableActionsButton,
      removeColumns: [ "defaultCustomer", "defaultDrop" ]
    });
  }, [ terminology, userTableActionsButton ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <LikeToDo
          copy="Setup a new user for this delivery point"
          buttons={createNewUser()}
        />

        <Table
          ref={TableRef}
          mainAction={row => history.push(`/admin/customers/users/${row.original.id}/details`)}
          error={usersResponse.error}
          tableOf={terminology.user.plural.text}
          loading={usersResponse.pending}
          data={usersResponse.data?.result ? usersResponse.data?.result : []}
          onFetchData={onFetchData}
          rowCount={usersResponse?.data?.count ?? 0}
          pageCount={usersResponse.data?.pages ?? 0}
          defaultPagination={defaultPagination}
          columns={columns}
        />

      </MainContentWrapper>

      <InfoBarWrapper>
        <DropInfoSidebar
          terminology={terminology}
          isLoading={isLoadingDrop}
          data={drop}
        />
      </InfoBarWrapper>

      <Modal
        small
        show={deleteUserModalData !== null}
        type="confirm"
        title="Are you sure?"
        body="Deleting this user cannot be undone."
        backgroundAction={() => setDeleteUserModalData(null)}
        actions={[
          {
            name: "No, cancel",
            action: () => setDeleteUserModalData(null),
            $buttonType: "secondary"
          },
          {
            name: "I confirm",
            $buttonType: "primary",
            action: () => {
              if (deleteUserModalData) {
                deleteUser({ id: deleteUserModalData.userId });
              }
            }
          }
        ]}
      />
    </Layout>
  );
};

export default DropUsersPage;
